import { normTemplate, templateSize, fitDims, addShelfLife, barWidthPx, code128Modules, MM_PER_PX, LABEL_PAD_MM, packLine } from '../components/LabelDesign';
import barcodeSvg from './barcodeSvg';
import { day } from './format';
import printDocument from './printDoc';

// Labels print through their own document (see printDoc), so the sticker size is
// the paper size and nothing else on the page can say otherwise.
//
// The page rules are the ones the shop supplied, with the size taken from the
// saved design rather than hard-coded: @page inside @media print, html and body
// pinned to the label, and .label given the size, the padding and Arial. A body
// left to grow reports a height the driver is free to round up, which is how a
// 30mm label came out over three stickers.

const esc = (v) => String(v == null ? '' : v)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// One label's inner markup — the same design the on-screen preview renders.
function labelInner(t, product, variant, mfgDate, D, cW, cH, rot, scale = 1) {
  const f = t.fields;
  const bestBefore = addShelfLife(mfgDate, product.shelfLife);
  const rows = [];
  const pack = packLine(f, variant);
  if (f.name) {
    // Combined, the pack and price sit after the name in lighter type; separate,
    // they get their own row below it.
    const tail = f.oneLine && pack ? `<span style="font-weight:400"> ${esc(pack)}</span>` : '';
    rows.push(`<div class="nm" style="font-size:${D.nameF}px">${esc(product.name)}${tail}</div>`);
  }
  if (pack && (!f.oneLine || !f.name)) rows.push(`<div class="mt1" style="font-size:${D.metaF + 1}px">${esc(pack)}</div>`);
  // Code 39 needs its own start/stop characters and a wider module, so the bar
  // width is worked out from whichever symbology is actually being printed.
  const code39 = t.symbology === 'CODE39';
  const modules = code39 ? (String(variant.barcode || '').length + 2) * 16 : code128Modules(variant.barcode);
  rows.push(barcodeSvg(variant.barcode, {
    format: t.symbology, height: D.barH, width: barWidthPx(modules, cW / MM_PER_PX),
    fontSize: D.barF, displayValue: f.barcodeNo,
  }));
  const dates = [];
  if (f.mfg) dates.push(`<div>Mfg: ${mfgDate ? esc(day(mfgDate)) : '—'}</div>`);
  if (f.shelfLife && product.shelfLife) dates.push(`<div>Shelf life: ${esc(product.shelfLife)}</div>`);
  if (f.bestBefore && bestBefore) dates.push(`<div>Best before: ${esc(day(bestBefore))}</div>`);
  if (dates.length) rows.push(`<div class="dt" style="font-size:${D.metaF}px">${dates.join('')}</div>`);
  if (t.companyLine) rows.push(`<div class="co" style="font-size:${D.metaF}px">${esc(t.companyLine)}</div>`);

  const turns = [rot ? 'rotate(90deg)' : '', scale !== 1 ? `scale(${scale})` : ''].filter(Boolean).join(' ');
  const transform = turns ? `transform:${turns};` : '';
  // Inside the padding, not over it: .label already reserves LABEL_PAD_MM a side.
  const iW = +(cW - 2 * LABEL_PAD_MM).toFixed(2);
  const iH = +(cH - 2 * LABEL_PAD_MM).toFixed(2);
  return `<div class="inner" style="width:${iW}mm;height:${iH}mm;${transform}">${rows.join('')}</div>`;
}

// The stylesheet, as given, with the label's own size in it.
//
// One deviation, and only for a run of more than one sticker: body takes
// min-height instead of height. A fixed 32mm body cannot hold ten labels, and
// the pages after the first would be dropped. For a single label — including
// every test print — it is the rule exactly as written.
function labelCss({ W, H, single, multi, continuous, roll }) {
  const pageRule = `@page { size: ${continuous ? `${W}mm auto` : roll ? `${W}mm ${H}mm` : 'A4'}; margin: 0; }`;
  return `
    ${pageRule}
    @media print {
      ${pageRule}

      html, body {
        width: ${W}mm;
        ${multi ? `min-height: ${H}mm;` : `height: ${H}mm;`}
        margin: 0;
        padding: 0;
      }

      .label {
        width: ${W}mm;
        height: ${H}mm;
        margin: 0;
        padding: 0.5mm;
        box-sizing: border-box;
        overflow: hidden;
        font-family: Arial, sans-serif;
      }
    }

    /* The same geometry outside @media print, so the document lays out the same
       way before the print stylesheet is applied. */
    html, body { width: ${W}mm; margin: 0; padding: 0; background: #fff; }
    body { font-family: Arial, sans-serif; color: #000; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .label {
      width: ${W}mm; height: ${H}mm; margin: 0; padding: 0.5mm; box-sizing: border-box;
      overflow: hidden; font-family: Arial, sans-serif;
      display: flex; align-items: center; justify-content: center;
      break-inside: avoid; page-break-inside: avoid;
    }
    /* Break AFTER every label except the last: a trailing break feeds one more
       blank sticker at the end of every run. A continuous run has no breaks at
       all — it is one page. */
    ${roll && !continuous ? '.label:not(:last-child) { page-break-after: always; break-after: page; }' : ''}
    .inner {
      box-sizing: border-box; overflow: hidden; text-align: center; width: 100%;
      transform-origin: center center;
      display: flex; flex-direction: column; align-items: stretch; justify-content: center;
    }
    /* One line, always — sized to fit by fitDims rather than wrapped. */
    .nm { font-weight: 700; line-height: 1.05; width: 100%; white-space: nowrap; overflow: hidden; }
    .mt1, .mt2 { color: #333; }
    .dt { line-height: 1.2; margin-top: 1px; }
    .co { color: #333; margin-top: 1px; line-height: 1.15; }
    /* NOT stretched. A bar is a whole number of print-head dots or the driver
       rounds it, and rounding some bars and not others is how a barcode stops
       scanning. It is sized to the label in whole dots instead. */
    svg { max-width: 100%; height: auto; display: block; margin: 0 auto; }
  `;
}

// One label, framed, to check the page against the sticker.
export function printTestLabel({ template }) {
  const t = normTemplate(template);
  const continuous = t.layout === 'continuous';
  const nominal = templateSize(t);
  const s = (Number(t.printScale) || 100) / 100;
  const W = +(nominal.W * s).toFixed(2);
  const H = +(nominal.H * s).toFixed(2);

  const body = `<div class="label"><div class="cal">
      <div class="cal-size">${nominal.W} × ${nominal.H} mm${s !== 1 ? ` @ ${t.printScale}%` : ''}</div>
      <div class="cal-note">frame should sit on the sticker edge</div>
    </div></div>`;

  const css = `${labelCss({ W, H, single: true, multi: false, continuous, roll: true })}
    .label { border: 0.2mm solid #000; }
    .cal { text-align: center; width: 100%; }
    .cal-size { font-size: 12px; font-weight: 700; }
    .cal-note { font-size: 7px; }
  `;

  return printDocument({ body, css, title: 'Label calibration' });
}

export function printLabels({ template, product, labels, mfgDate }) {
  const t = normTemplate(template);
  const nominal = templateSize(t);
  // Everything below is in PRINTED millimetres: the design's size times the
  // scale. Fitting still works against the design, so the layout does not change
  // when the scale does — the whole thing is simply drawn smaller.
  const s = (Number(t.printScale) || 100) / 100;
  const W = +(nominal.W * s).toFixed(2);
  const H = +(nominal.H * s).toFixed(2);
  const rot = t.rotate === 90;
  const cW = rot ? nominal.H : nominal.W;
  const cH = rot ? nominal.W : nominal.H;
  const continuous = t.layout === 'continuous';
  const roll = t.layout !== 'sheet';   // roll or continuous — both feed a roll
  const multi = labels.length > 1;

  // Each label is fitted for the product it carries: a long name shrinks its own
  // label rather than every label in the run.
  const body = labels
    .map((v) => {
      const D = fitDims({ t, product, variant: v, mfgDate, cW, cH });
      return `<div class="label">${labelInner(t, product, v, mfgDate, D, cW, cH, rot, s)}</div>`;
    })
    .join('');

  const css = labelCss({ W, H, single: !multi, multi, continuous, roll });
  return printDocument({ body, css, title: `${product.name} labels` });
}

export default printLabels;
